import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { requestExternalUploader } from '@ff-os/pdp-shared-redux/apis/external-upload/requestExternalUploader';
import { createSelectorBindings, ExternalUploadStatus, selectExternalUploadStatus } from '@ff-os/pdp-shared-redux/slices/externalUploadersSlice';
import { DeviceUpload } from '@ff-os/shared-react/components/upload-methods/partials/DeviceUpload';
import { selectPluginIdByVariableId } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { GiftExternalUploader, GiftExternalUploadPayload } from '@ff-scene-plugins/gift-redux/types/GiftExternalUploader';

const _GiftDeviceUpload = ({
    createExternalUploader,
    externalUploader,
    pluginId,
    status,
    onChange
}: Props) => {
    return (
        <DeviceUpload
            status={status}
            onChange={onChange}
            imageCount={externalUploader?.imageCount ?? 0}
            externalUploader={externalUploader}
            createExternalUploader={() => createExternalUploader(pluginId)} />
    );
};

type Props = OwnProps & {
    status: ExternalUploadStatus;
    externalUploader?: GiftExternalUploader;
    createExternalUploader: (pluginId: string) => void;
    pluginId: string;
};

type OwnProps = {
    variableIds: string[];
    multiple: boolean;
    accept: string;
    maxSize: number;
    onChange: () => void;
};

const mapStateToProps = (state: GiftPdpState, ownProps: OwnProps) => ({
    externalUploader: selectExternalUploaderByVariableIds(state, ownProps.variableIds),
    status: selectExternalUploadStatus(state),
    pluginId: selectPluginIdByVariableId(state, ownProps.variableIds[0])!
});

const mapDispatchToProps = (dispatch: GiftPdpDispatch, ownProps: OwnProps) => ({
    createExternalUploader: (pluginId: string) => dispatch(requestExternalUploader({
        payload: {
            variableIds: ownProps.variableIds,
            pluginId: pluginId
        },
        accept: ownProps.accept,
        allowMultipleFiles: ownProps.multiple,
        maxFileSize: ownProps.maxSize
    }))
});

export const GiftDeviceUpload = connect(
    mapStateToProps,
    mapDispatchToProps
)(_GiftDeviceUpload);

const selectors = createSelectorBindings<GiftExternalUploadPayload>();

export const selectExternalUploaderByVariableIds = createSelector([
    selectors.selectAll,
    (_: GiftPdpState, variableIds: string[]) => variableIds
], (externalUploaders, variableIds) => {
    return externalUploaders
        .filter(eu => eu.expiresOn > Date.now())
        .find(eu => allIdsAreTheSame(variableIds, eu.settings.payload.variableIds));
});

const allIdsAreTheSame = (a: string[], b: string[]) => {
    return a.every(id => b.includes(id));
};